import { Injectable, OnDestroy } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { FeatureState } from "@types";
import { ClientError, ErrorCode, isNotNullOrUndefined, isNumber } from "@utils";
import {
  BlobSource,
  BufferTarget,
  Conversion,
  Input,
  Mp4InputFormat,
  Mp4OutputFormat,
  Output,
} from "../../../../vendor/mediabunny-browser";
import { BehaviorSubject, EMPTY, Observable, of, Subject, timer } from "rxjs";
import { filter, first, map, switchMap, takeUntil } from "rxjs/operators";
import { AlertComponent } from "../components";
import { AccountService } from "./account.service";
import { AnalyticsService } from "./analytics.service";
import { ApiService } from "./api.service";

const MAX_RECORDING_DURATION_MILLISECONDS = 5 * 60 * 1000; // 5 minutes
const AUDIO_CHANNEL = "audio-channel";

@Injectable({
  providedIn: "root",
})
export class AudioRecordingService implements OnDestroy {
  private isRecordingSubject = new BehaviorSubject<boolean>(false);
  public isRecording$ = this.isRecordingSubject.asObservable();

  private isPlayingSubject = new BehaviorSubject<boolean>(false);
  public isPlaying$ = this.isPlayingSubject.asObservable();

  private recordedBlobSubject = new Subject<Blob>();
  public recordedBlob$ = this.recordedBlobSubject.asObservable();

  private recordingEndedSubject = new Subject<void>();
  public recordingEnded$ = this.recordingEndedSubject.asObservable();

  private recordingDurationSubject = new BehaviorSubject<number>(0);
  public recordingDuration$ = this.recordingDurationSubject.asObservable();

  private mediaRecorder: MediaRecorder | null = null;
  private mediaStream: MediaStream | null = null;
  private audioChunks: Blob[] = [];
  private audio: HTMLAudioElement | null = null;
  private stopTimer$ = new Subject<void>();
  private recordingStartTime: number = 0;
  private audioUrl: string | null = null;
  private audioChannel: BroadcastChannel;

  constructor(
    private apiService: ApiService,
    private accountService: AccountService,
    private matDialog: MatDialog,
    private analyticsService: AnalyticsService
  ) {
    this.audioChannel = new BroadcastChannel(AUDIO_CHANNEL);
    this.audioChannel.onmessage = () => {
      this.stopAudio(true);
    };
  }

  public ngOnDestroy(): void {
    this.stopMediaStream();
    this.deleteRecording();
    this.deleteAudio();
    this.audioChannel.close();
  }

  private stopAudio(skipBroadcast = false): void {
    if (!skipBroadcast) {
      this.audioChannel.postMessage("stop");
    }
    if (this.isRecordingSubject.value) {
      this.stopRecording();
    }
    if (this.isPlayingSubject.value) {
      this.pause();
    }
  }

  public startRecording(): Observable<HTMLAudioElement> {
    if (this.isRecordingSubject.value) {
      return EMPTY;
    }
    this.stopAudio();

    return new Observable((observer) => {
      (async () => {
        const permissionName: PermissionName = "microphone" as PermissionName;
        const permissionStatus = await navigator.permissions.query({
          name: permissionName,
        });
        if (permissionStatus.state === "denied") {
          observer.error(
            new ClientError(ErrorCode.ClientMicrophonePermissionDenied)
          );
          return;
        }

        try {
          const stream = await navigator.mediaDevices.getUserMedia({
            audio: true,
          });
          this.mediaStream = stream;
          this.mediaRecorder = new MediaRecorder(stream, {
            mimeType: "audio/mp4;codecs=mp4a.40.2",
          });

          this.mediaRecorder.ondataavailable = (event) => {
            this.audioChunks.push(event.data);
          };

          this.mediaRecorder.onstop = async () => {
            let audioBlob = new Blob(this.audioChunks, {
              type: "audio/mp4",
            });

            const input = new Input({
              formats: [new Mp4InputFormat()],
              source: new BlobSource(audioBlob),
            });

            const output = new Output({
              format: new Mp4OutputFormat(),
              target: new BufferTarget(),
            });

            const conversion = await Conversion.init({
              input,
              output,
              audio: {
                codec: 'aac',
              }
            });

            await conversion.execute();

            audioBlob = new Blob([output.target.buffer], {
              type: "audio/mp4"
            });

            this.recordedBlobSubject.next(audioBlob);
            this.audioChunks = [];
            this.stopTimer$.next();
            this.isRecordingSubject.next(false);
            this.loadBlob(audioBlob).subscribe((audio) => {
              this.audio = audio;
              this.recordingDurationSubject.next(audio.duration * 1000);
              this.stopMediaStream();
              observer.next(audio);
              observer.complete();
            });
          };

          this.mediaRecorder.start();
          this.isRecordingSubject.next(true);
          this.recordingStartTime = Date.now();

          timer(0, 100)
            .pipe(takeUntil(this.stopTimer$))
            .subscribe(() => {
              const elapsed = Date.now() - this.recordingStartTime;
              this.recordingDurationSubject.next(elapsed);
            });

          this.accountService.userLimits$
            .pipe(
              filter(isNotNullOrUndefined),
              first(),
              switchMap((limits) => {
                return timer(limits.voice_messages.maxLengthSeconds * 1000).pipe(
                  map((_) => {
                    return limits.voice_messages;
                  })
                );
              }),
              takeUntil(this.stopTimer$)
            )
            .subscribe(({ featureState, promptThrottleIntervalMinutes }) => {
              this.stopRecording();

              if (featureState !== FeatureState.Upgradeable) return;

              const lastShownTimeString = localStorage.getItem(
                "voice_prompt_display_time"
              );
              if (!isNumber(lastShownTimeString)) return;
              const lastShownTime = Number(lastShownTimeString);
              const lastShownMinutesAgo = (Date.now() - lastShownTime) / 60000;

              if (lastShownMinutesAgo > promptThrottleIntervalMinutes) {
                localStorage.setItem(
                  "voice_prompt_display_time",
                  Date.now().toString()
                );

                const dialogRef = AlertComponent.openDialog(this.matDialog, {
                  title: "You've hit the recording time limit",
                  message: "Upgrade to extend your voice message.",
                  acceptButtonText: "Upgrade",
                  acceptOnly: true,
                });

                dialogRef.afterClosed().subscribe({
                  next: (isAccepted) => {
                    if (!isAccepted) return;
                    window.open("https://www.celohealth.com/plans", "_blank");
                  },
                });

                dialogRef
                  .afterOpened()
                  .pipe(
                    switchMap(() => {
                      return this.analyticsService.logEvents([
                        {
                          eventType: "PlanSummary",
                          timestamp: new Date().toISOString(),
                          referrer: "VoiceMessageLimit",
                        },
                      ]);
                    })
                  )
                  .subscribe();
              }
            });
        } catch (err) {
          if (err.name === "NotAllowedError") {
            throw new Error(ErrorCode.ClientMicrophonePermissionDenied);
          }
          console.error("Error starting recording:", err);
          observer.error(err);
        }
      })();
    });
  }

  public stopRecording() {
    if (this.mediaRecorder && this.isRecordingSubject.value) {
      this.mediaRecorder.stop();
    }
  }

  public loadUrl(
    url: string,
    seekSeconds: number = 0
  ): Observable<HTMLAudioElement> {
    if (this.audioUrl === url) {
      return of(this.audio);
    }
    this.deleteAudio();
    this.audioUrl = url;
    return this.apiService
      .request("get", { path: url }, { responseType: "blob" })
      .pipe(switchMap((blob) => this.loadBlob(blob, seekSeconds)));
  }

  public loadBlob(
    blob: Blob,
    seekSeconds: number = 0
  ): Observable<HTMLAudioElement> {
    return new Observable((observer) => {
      this.deleteAudio();
      const url = URL.createObjectURL(blob);
      this.audio = new Audio(url);
      this.audio.preload = "auto";
      this.audio.currentTime = seekSeconds;

      this.audio.onloadedmetadata = () => {
        observer.next(this.audio);
        observer.complete();
      };

      this.audio.onplaying = () => {
        this.isPlayingSubject.next(true);
      };

      this.audio.onpause = () => {
        this.isPlayingSubject.next(false);
      };

      this.audio.onended = () => {
        this.isPlayingSubject.next(false);
        this.audio.currentTime = 0;
        this.recordingEndedSubject.next();
      };
    });
  }

  public play(mediaUrl?: string): Observable<HTMLAudioElement> {
    this.stopAudio();

    if (this.audio && !mediaUrl) {
      if (this.isPlayingSubject.value) {
        this.pause();
      } else {
        this.audio.play();
      }
      return of(this.audio);
    }

    if (mediaUrl) {
      return new Observable((observer) => {
        this.loadUrl(mediaUrl).subscribe((audio) => {
          audio.play();
          observer.next(audio);
          observer.complete();
        });
      });
    }

    return EMPTY;
  }

  public pause(): void {
    if (this.audio && this.isPlayingSubject.value) {
      this.audio.pause();
    }
  }

  public seek(milliseconds: number): void {
    if (this.audio) {
      this.audio.currentTime = milliseconds / 1000;
    }
  }

  private stopMediaStream(): void {
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach((track) => track.stop());
      this.mediaStream = null;
    }
  }

  public deleteRecording(): void {
    this.stopTimer$.next();
    if (this.isRecordingSubject.value) {
      this.mediaRecorder.ondataavailable = null;
      this.mediaRecorder.onstop = null;
      this.mediaRecorder.stop();
    } else {
      this.deleteAudio();
    }

    this.stopMediaStream();
    this.isRecordingSubject.next(false);
    this.isPlayingSubject.next(false);
    this.recordedBlobSubject.next(null);
    this.recordingDurationSubject.next(0);
    this.audioChunks = [];
  }

  private deleteAudio(): void {
    if (this.audio) {
      this.audio.pause();
      if (this.audioUrl && this.audioUrl.startsWith("blob:")) {
        URL.revokeObjectURL(this.audio.src);
      }
      this.audio = null;
      this.audioUrl = null;
    }
  }
}
